import { SyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Show,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import Image from "next/image";
import { BsArrowLeft, BsArrowUpRight, BsTwitter } from "react-icons/bs";
import { FaDiscord, FaTelegramPlane } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import { commify } from "@ethersproject/units";

// Images
import bannerImage from "assets/images/details/bannerImage.svg";
import bannerImageMobile from "assets/images/details/bannerImageMobile.svg";
import staderLogo from "assets/svg/stader_logo_dark.svg";

// utils
import { validateEmail } from "utils/common";
import { trackEvent } from "utils/firebase";

// Constants
import {
  MATICX_AUDIT,
  MATICX_BUG_BOUNTY,
  MATICX_SOCIALS,
  PRIMARY_TABS,
  STAKE_NOW_CTA,
  SUBSCRIBE_EMAIL,
  SUBSCRIBE_PRIVACY,
  VIEW_OUR_PARTNERS,
} from "constants/analytics";
import {
  angels,
  auditInfo,
  blockchainFunds,
  bugBountyInfo,
  defiEcosystem,
  drawerNavItems,
  funds,
  howItWorks,
  maticxHighlights,
  navItemEventMapping,
  navItems,
  partnerInfo,
  MEET,
  WORK,
  DEFI,
  SAFE,
  PARTNERS,
  COMMUNITY,
  SUBSCRIBE,
} from "constants/blockChainDetails";
import {
  DISCORD_URL,
  STADER_URL,
  TELEGRAM_URL,
  TERMS_URL,
  TWITTER_URL,
} from "constants/constants";

// services
import { subscribeEmail } from "services/common";
import { getTVL } from "services/maticx";
import Head from "next/head";
import dynamic from "next/dynamic";

const Link = dynamic(() => import("components/Link"), {
  ssr: false,
});

const PageHead = () => {
  return (
    <Head>
      <title>Polygon Liquid Staking</title>
    </Head>
  );
};

let timerId = -1;

const BlockchainInfo = () => {
  const [tvl, setTVL] = useState<Number>();
  const [isTVLLoading, setTVLLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isNavigating, setNavigating] = useState(false);

  const [activeMenu, setActiveMenu] = useState(navItems[0].id);
  const [activeDrawerMenu, setActiveDrawerMenu] = useState(
    drawerNavItems[0].id
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  useEffect(() => {
    if (!isOpen) {
      setActiveDrawerMenu(drawerNavItems[0].id);
    }
  }, [isOpen]);

  useEffect(() => {
    (async function fetchTVL() {
      const response = await getTVL();

      setTVL(response);
      setTVLLoading(false);
    })();
  });

  useEffect(() => {
    if (isNavigating) {
      clearTimeout(timerId);
      const element = document.querySelector<HTMLElement>(`#${activeMenu}`);

      element?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });

      timerId = window.setTimeout(() => setNavigating(false), 3000);
    }
  }, [activeMenu, isNavigating]);

  useEffect(() => {
    if (isNavigating && isOpen) {
      clearTimeout(timerId);
      const element = document.querySelector<HTMLElement>(
        `#${activeDrawerMenu}`
      );

      element?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });

      timerId = window.setTimeout(() => setNavigating(false), 3000);
    }
  }, [activeDrawerMenu, isNavigating, isOpen]);

  useEffect(() => {
    if (email) {
      const isValidEmail = validateEmail(email);

      if (isValidEmail) {
        setError("");
      } else {
        setError("Please enter correct email ID");
      }
    }
  }, [email]);

  useEffect(() => {
    if (!isNavigating && !isOpen) {
      const mainElements = navItems.map(({ id }) =>
        document.getElementById(id)
      );

      const observer = new IntersectionObserver(
        (entries) => {
          for (let entry of entries) {
            if (entry.isIntersecting && !isNavigating) {
              trackEvent(navItemEventMapping[entry.target.id]);
              setActiveMenu(entry.target.id);
            }
          }
        },
        {
          threshold: 0.5,
        }
      );

      mainElements.forEach((element) => element && observer.observe(element));

      // cleanup function
      return () => {
        mainElements.forEach(
          (element) => element && observer.unobserve(element)
        );
      };
    }
  }, [isNavigating, isOpen]);

  useEffect(() => {
    if (isOpen && !isNavigating) {
      const drawerElements = drawerNavItems.map(({ id }) =>
        document.getElementById(id)
      );

      const observer = new IntersectionObserver(
        (entries) => {
          for (let entry of entries) {
            if (entry.isIntersecting && !isNavigating) {
              setActiveDrawerMenu(entry.target.id);
            }
          }
        },
        {
          threshold: 0.9,
        }
      );
      if (!isNavigating) {
        drawerElements.forEach(
          (element) => element && observer.observe(element)
        );
      }

      // cleanup function
      return () => {
        drawerElements.forEach(
          (element) => element && observer.unobserve(element)
        );
      };
    }
  }, [isOpen, isNavigating]);

  const onStakeClick = (location: string) => {
    trackEvent(STAKE_NOW_CTA, { location });
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    trackEvent(SUBSCRIBE_EMAIL, { email_id: email });
    if (!validateEmail(email)) {
      setError("Please enter correct email ID");
      return;
    }

    setIsSubscribing(true);

    const formData: any = new FormData();

    formData.append("Email", email);
    formData.append("Polygon", true);

    const response = await subscribeEmail(formData);

    if ((response as any).error) {
      toast({
        description: "Failed to subscribe",
        status: "error",
        duration: 2000,
        position: "top",
        isClosable: true,
      });
      setIsSubscribing(false);

      return;
    }

    toast({
      description: "Email subscribed successfully",
      status: "success",
      duration: 2000,
      position: "top",
      isClosable: true,
    });
    setIsSubscribing(false);
  };

  const trackCommunityClick = (type: string) => {
    trackEvent(MATICX_SOCIALS, { social_type: type });
  };

  return (
    <>
      <PageHead />
      <Center
        bg="black"
        flexDir="column"
        w="100vw"
        pb="2rem"
        px={{ base: "16px", xl: "0" }}
        color="white"
      >
        <Box
          w={{ base: "100%", xl: "996px" }}
          mb={{ base: "16px", xl: "106px" }}
          pt="2.5rem"
        >
          <Flex align="center" gap="0.5rem" as="a" href={STADER_URL}>
            <Image src={staderLogo} alt="stader-icon" priority />
            <Text
              fontSize="1.5rem"
              fontFamily="neue-bold"
              // display={{ base: "none", xl: "block" }}
            >
              Stader
            </Text>
          </Flex>
          <Flex
            flexDir={{ base: "column-reverse", xl: "row" }}
            w="100%"
            justify="space-between"
            mt={{ base: 0, xl: "72px" }}
          >
            <Center
              flexDir="column"
              justifyContent={{ base: "center", xl: "flex-start" }}
              alignItems={{ base: "center", xl: "start" }}
            >
              <Text
                fontSize={{ base: 36, xl: 56 }}
                fontFamily="semi-bold"
                lineHeight="110%"
                mt={{ base: 16, xl: 0 }}
              >
                MaticX
              </Text>
              <Text
                color="brand.textSec"
                w={{ base: "100%", xl: "486px" }}
                lineHeight="24px"
                mt="1rem"
                textAlign={{ base: "center", xl: "left" }}
              >
                Stake and Earn staking rewards with Stader&apos;s liquid staking
                token, MaticX. Access instant liquidity and explore exciting
                DeFi opportunities across the Polygon ecosystem.
              </Text>
              <Flex
                w="100%"
                gap="3rem"
                mt="1rem"
                justifyContent={{ base: "center", xl: "flex-start" }}
              >
                <Box textAlign="center">
                  {!isTVLLoading ? (
                    <Text
                      fontSize={{ base: 18, xl: 24 }}
                      fontFamily="medium"
                      lineHeight="160%"
                    >
                      {tvl ? commify(+tvl) : "-"}{" "}
                      <Text as="span" fontSize={16}>
                        Matic
                      </Text>
                    </Text>
                  ) : (
                    <Spinner color="white" size="lg" />
                  )}
                  <Text
                    fontSize={{ base: 12, xl: 16 }}
                    fontFamily="medium"
                    lineHeight="160%"
                    color="brand.textSec"
                  >
                    TVL Staked
                  </Text>
                </Box>
                <Box textAlign="center">
                  {!isTVLLoading ? (
                    <Text
                      fontSize={{ base: 18, xl: 24 }}
                      fontFamily="medium"
                      lineHeight="160%"
                    >
                      5.76 %
                    </Text>
                  ) : (
                    <Spinner color="white" size="lg" />
                  )}
                  <Text
                    fontSize={{ base: 12, xl: 16 }}
                    fontFamily="medium"
                    lineHeight="160%"
                    color="brand.textSec"
                  >
                    Staking rewards %
                  </Text>
                </Box>
              </Flex>
              <Link
                link="/liquid-staking/maticx"
                fontFamily="semi-bold"
                mt="2.5rem"
                bg="white"
                color="black"
                p="12px 45px"
                fontSize={16}
                borderRadius={8}
                _hover={{
                  color: "white",
                  bg: "brand.primary",
                }}
                onClick={() => onStakeClick("hero")}
              >
                Stake now
              </Link>
            </Center>
            <Show above="xl">
              <Center mt="-3rem">
                <Image src={bannerImage} alt="banner-image" priority />
              </Center>
            </Show>
            <Show below="xl">
              <Center mt="2.5rem">
                <Image src={bannerImageMobile} alt="banner-image" priority />
              </Center>
            </Show>
          </Flex>
        </Box>
        <Center
          w="100%"
          flexDir="column"
          pos="relative"
          position="sticky"
          top="0"
          zIndex={1}
        >
          <Flex
            w={{ base: "100%", xl: "996px" }}
            gap="1rem"
            overflowX="auto"
            className="scroll-container"
            pt="1.5rem"
            bg="black"
          >
            {navItems.map(({ id, label }) => (
              <Center
                key={id}
                id={`${id}-tab`}
                cursor="pointer"
                flexDir="column"
                p="0"
                px={{ base: "1rem", xl: "2rem" }}
                minW="fit-content"
                onClick={() => {
                  trackEvent(PRIMARY_TABS, { tab_name: id });
                  setNavigating(true);
                  setActiveMenu(id);
                }}
                fontSize={{ base: 12, xl: 16 }}
              >
                <Text
                  color={activeMenu === id ? "white" : "brand.textSec"}
                  mb="1rem"
                >
                  {label}
                </Text>
                <Divider
                  w={{ base: "40px", xl: "80px" }}
                  border="2px solid"
                  borderColor={activeMenu === id ? "white" : "black"}
                  borderTopRadius={8}
                />
              </Center>
            ))}
          </Flex>
          <Divider border="1px solid" borderColor="#424242" w="100vw" />
        </Center>
        <Box w={{ base: "100%", xl: "996px" }}>
          <Center
            pt={{ base: "80px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="80px"
            id={MEET}
          >
            <Text
              fontSize={{ base: 32, xl: 54 }}
              fontFamily="bold"
              textAlign="center"
            >
              Meet MaticX
            </Text>
            <SimpleGrid columns={{ base: 1, xl: 3 }} mt="4rem" gap="1rem">
              {maticxHighlights.map(({ icon, title, description }) => (
                <Flex flexDir="column" align="center" key={title}>
                  <Image src={icon} alt="token-image" />
                  <Text
                    fontSize={{ base: 16, xl: 24 }}
                    fontFamily={{ base: "semi-bold", xl: "bold" }}
                    mt="2rem"
                  >
                    {title}
                  </Text>
                  <Text
                    fontSize={{ base: 12, xl: 16 }}
                    textAlign="center"
                    mt="12px"
                    color="brand.textSec"
                    maxW={{ base: "240px", xl: "310px" }}
                  >
                    {description}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="80px"
            id={WORK}
          >
            <Text
              fontSize={{ base: 32, xl: 54 }}
              fontFamily="bold"
              textAlign="center"
            >
              How MaticX works
            </Text>
            <SimpleGrid columns={{ base: 1, xl: 3 }} mt="4rem" gap="1rem">
              {howItWorks.map(({ icon, title, description }) => (
                <Flex flexDir="column" align="center" key={title}>
                  <Image src={icon} alt="token-image" />
                  <Text
                    fontSize={{ base: 16, xl: 24 }}
                    fontFamily={{ base: "semi-bold", xl: "bold" }}
                    mt="2rem"
                  >
                    {title}
                  </Text>
                  <Text
                    fontSize={{ base: 12, xl: 16 }}
                    textAlign="center"
                    mt="12px"
                    color="brand.textSec"
                    maxW={{ base: "240px", xl: "310px" }}
                  >
                    {description}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>

            <Link
              link="/liquid-staking/maticx"
              fontFamily="semi-bold"
              mt="4rem"
              bg="white"
              color="black"
              p="12px 45px"
              fontSize={16}
              borderRadius={8}
              _hover={{
                color: "white",
                bg: "brand.primary",
              }}
              onClick={() => onStakeClick("how_it_works")}
            >
              Stake now
            </Link>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="80px"
            id={DEFI}
          >
            <Text
              fontSize={{ base: 32, xl: 54 }}
              fontFamily="bold"
              textAlign="center"
            >
              MaticX DeFi ecosystem
            </Text>
            <SimpleGrid
              columns={{ base: 1, md: 2, xl: 4 }}
              mt="4rem"
              gap="1rem"
              w="100%"
            >
              {defiEcosystem.map(({ icon, title }) => (
                <Flex
                  align="center"
                  gap="1rem"
                  key={title}
                  w={{ base: "100%", xl: 231 }}
                  p={{ base: "16px", xl: "24px" }}
                  bg="#202022"
                  borderRadius={16}
                >
                  <Image src={icon} alt="rocket-image" width={48} height={48} />
                  <Text
                    fontSize={{ base: 16, xl: 16 }}
                    fontFamily={{ base: "semi-bold", xl: "bold" }}
                    color="brand.textSec"
                  >
                    {title}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="80px"
            id={SAFE}
          >
            <Text
              fontSize={{ base: 32, xl: 54 }}
              fontFamily="bold"
              textAlign="center"
            >
              Security
            </Text>

            <Text
              fontSize={{ base: 12, xl: 24 }}
              fontFamily="medium"
              textAlign="center"
              color="brand.textSec"
              w={{ base: 240, xl: "100%" }}
            >
              Audited by top security firms
            </Text>
            <Box w={{ base: "100%", xl: "fit-content" }}>
              <SimpleGrid
                columns={{ base: 1, lg: 2 }}
                mt="4rem"
                gap={{ base: "1rem", lg: "1.5rem" }}
                w={{ base: "100%", xl: "fit-content" }}
              >
                {auditInfo.map(({ icon, title, link }) => (
                  <Flex
                    key={title}
                    flexDir={{ base: "row", xl: "column" }}
                    justify={{ base: "space-between", xl: "center" }}
                    align="center"
                    w={{ base: "100%", xl: 316 }}
                    h={{ base: "inherit", xl: 318 }}
                    bg="#202022"
                    p="1rem"
                    borderRadius={{ base: 12, xl: 24 }}
                  >
                    <Flex
                      w="100%"
                      flexDir={{ base: "row", xl: "column" }}
                      align="center"
                      gap="1rem"
                    >
                      <Box w={{ base: "40px", xl: "88px" }}>
                        <Image src={icon} alt={`${title}-image`} />
                      </Box>
                      <Text
                        fontSize={{ base: 16, xl: 24 }}
                        fontFamily={{ base: "semi-bold", xl: "bold" }}
                      >
                        {title}
                      </Text>
                    </Flex>
                    <Box
                      as="a"
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        trackEvent(MATICX_AUDIT, { audit_type: title });
                      }}
                    >
                      <Show above="xl">
                        <Text
                          fontSize={14}
                          textAlign="center"
                          mt="2.5rem"
                          maxW={{ base: "240px", xl: "310px" }}
                          borderBottom="1px dashed white"
                        >
                          View audit report
                        </Text>
                      </Show>
                      <Show below="xl">
                        <Text
                          fontSize={20}
                          fontFamily="bold"
                          textAlign="center"
                        >
                          <FiChevronRight />
                        </Text>
                      </Show>
                    </Box>
                  </Flex>
                ))}
              </SimpleGrid>
              {bugBountyInfo.map(({ icon, title, link, description }) => (
                <Flex
                  key={title}
                  mt="1.5rem"
                  justify="space-between"
                  align="center"
                  w="100%"
                  bg="#202022"
                  p={{ base: "1rem 1.5rem", xl: "2rem 2.5rem" }}
                  borderRadius={{ base: 12, xl: 24 }}
                >
                  <Flex
                    w="100%"
                    flex={{ base: "row", xl: "column" }}
                    align="center"
                    gap="1rem"
                  >
                    <Box w={{ base: "40px", xl: "64px" }}>
                      <Image src={icon} alt={`${title}-image`} />
                    </Box>
                    <Box>
                      <Text
                        fontSize={{ base: 16, xl: 16 }}
                        fontFamily={{ base: "semi-bold", xl: "bold" }}
                      >
                        {title}
                      </Text>
                      <Text
                        fontSize={{ base: 12, xl: 14 }}
                        color="brand.textSec"
                        w={{ base: 168, xl: "100%" }}
                      >
                        {description}
                      </Text>
                    </Box>
                  </Flex>
                  <Box
                    as="a"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      trackEvent(MATICX_BUG_BOUNTY, { bug_bounty_type: title });
                    }}
                  >
                    <Show above="xl">
                      <Text fontSize={14} borderBottom="1px dashed white">
                        Visit
                      </Text>
                    </Show>
                    <Show below="xl">
                      <Text fontSize={20} fontFamily="bold" textAlign="center">
                        <FiChevronRight />
                      </Text>
                    </Show>
                  </Box>
                </Flex>
              ))}
            </Box>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="80px"
            id={PARTNERS}
          >
            <Text
              fontSize={{ base: 32, xl: 54 }}
              fontFamily="bold"
              textAlign="center"
            >
              Our partners
            </Text>
            <Flex
              gap="1rem"
              mt="1.5rem"
              fontSize={{ base: 12, xl: 16 }}
              color={{ base: "brand.textSec", md: "white" }}
            >
              <Flex align="center" gap="0.5rem">
                <Show above="md">
                  <Image src={partnerInfo.funds.icon} alt="funds-image" />
                </Show>
                <Show below="md">
                  <Image src={partnerInfo.funds.iconMobile} alt="funds-image" />
                </Show>
                <Text>{partnerInfo.funds.text}</Text>
              </Flex>
              <Flex align="center" gap="0.5rem">
                <Show above="md">
                  <Image src={partnerInfo.blockChains.icon} alt="chain-image" />
                </Show>
                <Show below="md">
                  <Image
                    src={partnerInfo.blockChains.iconMobile}
                    alt="chain-image"
                  />
                </Show>
                <Text>{partnerInfo.blockChains.text}</Text>
              </Flex>
              <Flex align="center" gap="0.5rem">
                <Show above="md">
                  <Image src={partnerInfo.angels.icon} alt="angels-image" />
                </Show>
                <Show below="md">
                  <Image
                    src={partnerInfo.angels.iconMobile}
                    alt="angels-image"
                  />
                </Show>

                <Text>{partnerInfo.angels.text}</Text>
              </Flex>
            </Flex>
            <Flex
              mt="3rem"
              justify="center"
              w="100%"
              flexWrap="wrap"
              gap="1rem"
            >
              {partnerInfo.partners.map((icon, index) => (
                <Center
                  key={index}
                  w={{ base: 99, lg: 180 }}
                  bg="#202022"
                  borderRadius={8}
                >
                  <Image src={icon} alt="rocket-image" />
                </Center>
              ))}
            </Flex>
            <Button
              fontFamily="semi-bold"
              mt="4rem"
              bg="white"
              color="black"
              p="1.5rem 2rem"
              fontSize={16}
              borderRadius={8}
              _hover={{
                color: "white",
                bg: "brand.primary",
              }}
              onClick={() => {
                trackEvent(VIEW_OUR_PARTNERS);
                onOpen();
              }}
            >
              View our partners
            </Button>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            scrollMarginTop="120px"
            id={COMMUNITY}
          >
            <SimpleGrid
              columns={{ base: 1, xl: 2 }}
              w={{ base: "100%", xl: "836px" }}
            >
              <Center>
                <Text
                  fontSize={{ base: 32, xl: 40 }}
                  fontFamily="bold"
                  textAlign="center"
                >
                  Meet the worldwide community
                </Text>
              </Center>
              <Flex
                flexDir="column"
                gap="43px"
                align={{ base: "center", xl: "end" }}
                mt={{ base: "43px", xl: "inherit" }}
              >
                <Flex
                  w="185px"
                  as="a"
                  align="center"
                  fontSize={{ base: 20, xl: 24 }}
                  gap="1rem"
                  href={TWITTER_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackCommunityClick("Twitter")}
                >
                  <BsTwitter fontSize={32} />
                  <Text fontFamily="bold">Twitter</Text>
                  <BsArrowUpRight fontSize={16} />
                </Flex>
                <Flex
                  w="185px"
                  as="a"
                  align="center"
                  fontSize={{ base: 20, xl: 24 }}
                  gap="1rem"
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackCommunityClick("Telegram")}
                >
                  <Box bg="white" color="black" fontSize={32}>
                    <FaTelegramPlane />
                  </Box>
                  <Text fontFamily="bold">Telegram</Text>
                  <BsArrowUpRight fontSize={16} />
                </Flex>
                <Flex
                  w="185px"
                  as="a"
                  align="center"
                  fontSize={{ base: 20, xl: 24 }}
                  gap="1rem"
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackCommunityClick("Discord")}
                >
                  <FaDiscord fontSize={32} />
                  <Text fontFamily="bold">Discord</Text>
                  <BsArrowUpRight fontSize={16} />
                </Flex>
              </Flex>
            </SimpleGrid>
          </Center>
          <Center
            mt={{ base: "64px", xl: "160px" }}
            flexDir="column"
            pb="5rem"
            scrollMarginTop="80px"
            id={SUBSCRIBE}
          >
            <Text
              fontSize={40}
              fontFamily="bold"
              maxW="280px"
              textAlign="center"
              lineHeight="110%"
            >
              Subscribe to our mailing list
            </Text>

            <Center flexDir="column" mt="2.5rem" w="100%">
              <form onSubmit={handleSubmit}>
                <InputGroup
                  size="lg"
                  mt="0.5rem"
                  mb="0"
                  w={{ base: "340px", lg: "656px" }}
                >
                  <Input
                    bg="#202022"
                    color="white"
                    border="none"
                    type="email"
                    required
                    placeholder="Email address"
                    value={email}
                    fontSize="1rem"
                    h={{ base: "56px", xl: "72px" }}
                    pl="2rem"
                    borderRadius="16px"
                    onChange={(e) => setEmail(e.target.value)}
                    _placeholder={{ color: "#766A6A" }}
                  />

                  <InputRightElement h="72px" w="128px" mr="0.5rem">
                    <Show above="xl">
                      <Button
                        type="submit"
                        bg={isSubscribing ? "brand.primary" : "white"}
                        color="black"
                        h="48px"
                        w="128px"
                        p="1.5rem"
                        // disabled={!email || !!error || isSubscribing}
                        _hover={{
                          bg: "brand.primary",
                          color: "white",
                        }}
                      >
                        {isSubscribing ? (
                          <Spinner size="md" color="white" />
                        ) : (
                          <Text> Subscribe</Text>
                        )}
                      </Button>
                    </Show>
                  </InputRightElement>
                </InputGroup>
                {error && (
                  <Text
                    fontWeight="400"
                    fontSize="14px"
                    color="#e53e3e"
                    mt="0.5rem"
                  >
                    {error}
                  </Text>
                )}
                <Show below="xl">
                  <Center>
                    <Button
                      w="152px"
                      type="submit"
                      mt="1.5rem"
                      bg={isSubscribing ? "brand.primary" : "white"}
                      color="black"
                      h="40px"
                      p="10px 2.5rem"
                      _hover={{
                        bg: "brand.primary",
                        color: "white",
                      }}
                      // disabled={!email || !!error || isSubscribing}
                    >
                      {isSubscribing ? (
                        <Spinner size="md" color="white" />
                      ) : (
                        <Text>Subscribe</Text>
                      )}
                    </Button>
                  </Center>
                </Show>
              </form>
              <Center mt="1.5rem" flexDir={{ base: "column", xl: "row" }}>
                <Text color="brand.textSec" mr="6px">
                  By subscribing you accept our
                </Text>
                <Text
                  as="a"
                  color="white"
                  borderBottom="1px dashed"
                  href={TERMS_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent(SUBSCRIBE_PRIVACY)}
                >
                  Privacy policy
                </Text>
              </Center>
            </Center>
          </Center>
        </Box>
      </Center>
      <Drawer onClose={onClose} isOpen={isOpen} size="full">
        <DrawerOverlay />
        <DrawerContent bg="black" color="white">
          <DrawerBody pb="4rem">
            <Center flexDir="column">
              <Center
                w="100%"
                flexDir="column"
                pos="relative"
                position="sticky"
                top="-2"
                zIndex={1}
              >
                <Box w={{ base: "100%", xl: "996px" }}>
                  <Flex pt="2.5rem" align="center" gap="2rem" bg="black">
                    <Box
                      fontSize={{ base: 24, xl: 40 }}
                      onClick={onClose}
                      cursor="pointer"
                    >
                      <BsArrowLeft />
                    </Box>

                    <Text
                      fontSize={{ base: 24, xl: 40 }}
                      fontFamily="semi-bold"
                    >
                      Our partners
                    </Text>
                  </Flex>
                </Box>
                <Flex
                  w={{ base: "100%", xl: "996px" }}
                  gap="1rem"
                  overflowX="auto"
                  className="scroll-container"
                  pt="1.5rem"
                  bg="black"
                >
                  {drawerNavItems.map(({ id, label }) => (
                    <Center
                      cursor="pointer"
                      key={id}
                      flexDir="column"
                      p="0"
                      px={{ base: "1rem", xl: "2rem" }}
                      minW="fit-content"
                      onClick={() => {
                        setNavigating(true);
                        setActiveDrawerMenu(id);
                      }}
                      fontSize={{ base: 12, xl: 16 }}
                    >
                      <Text
                        color={
                          activeDrawerMenu === id ? "white" : "brand.textSec"
                        }
                        mb="1rem"
                      >
                        {label}
                      </Text>
                      <Divider
                        w="80%"
                        border="2px solid"
                        borderColor={
                          activeDrawerMenu === id ? "white" : "black"
                        }
                        borderTopRadius={8}
                      />
                    </Center>
                  ))}
                </Flex>
                <Divider border="1px solid" borderColor="#424242" w="100vw" />
              </Center>
              <Box
                mt={{ base: "40px", xl: "120px" }}
                scrollMarginTop="260px"
                id="funds"
              />
              <Center flexDir="column" w={{ base: "100%", xl: "996px" }}>
                <Flex justify="center" w="100%" flexWrap="wrap" gap="1rem">
                  {funds.map((icon, index) => (
                    <Center
                      key={index}
                      w={{ base: "100%", lg: 231 }}
                      bg="#202022"
                      borderRadius={8}
                    >
                      <Image src={icon} alt="rocket-image" />
                    </Center>
                  ))}
                </Flex>
                <Box
                  mt={{ base: "60px", xl: "160px" }}
                  scrollMarginTop="200px"
                  id="blockchains"
                  textAlign="center"
                >
                  <Text fontSize={{ base: 20, xl: 24 }} fontFamily="semi-bold">
                    Blockchains
                  </Text>
                  <Flex
                    justify="center"
                    w="100%"
                    flexWrap="wrap"
                    gap="1rem"
                    mt="40px"
                  >
                    {blockchainFunds.map((icon, index) => (
                      <Center
                        key={index}
                        w={{ base: "100%", lg: 231 }}
                        bg="#202022"
                        borderRadius={8}
                      >
                        <Image src={icon} alt="rocket-image" />
                      </Center>
                    ))}
                  </Flex>
                </Box>
                <Box
                  mt={{ base: "60px", xl: "160px" }}
                  textAlign="center"
                  scrollMarginTop="200px"
                  id="angels"
                >
                  <Text fontSize={{ base: 20, xl: 24 }} fontFamily="semi-bold">
                    Angels
                  </Text>
                  <Flex
                    mt="40px"
                    justify="center"
                    w="100%"
                    flexWrap="wrap"
                    gap="1rem"
                  >
                    {angels.map(({ name, designation }) => (
                      <Center
                        key={name}
                        flexDir="column"
                        w={{ base: "100%", lg: 316 }}
                        bg="#202022"
                        borderRadius={8}
                        py="1rem"
                      >
                        <Text fontFamily="medium">{name}</Text>
                        <Text fontSize={12}>{designation}</Text>
                      </Center>
                    ))}
                  </Flex>
                </Box>
              </Center>
            </Center>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

BlockchainInfo.isLandingPage = true;

export default BlockchainInfo;
